'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { useDict } from '@/lib'
import { formatDate } from '@/utils/formatDate'
import s from './MaintenancePage.module.scss'
import { useZebraLogoSrc } from '@/hooks/useZebraLogoSrc'

interface MaintenancePageProps {
  type: 'debut' | 'maintenance'
  targetDate?: Date
}

export function MaintenancePage({ type, targetDate }: MaintenancePageProps) {
  const zebraLogoSrc = useZebraLogoSrc()
  const dict = useDict('maintenance-page')
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null)

  useEffect(() => {
    if (!targetDate) return

    const calculateTimeRemaining = () => {
      const now = new Date()
      const difference = targetDate.getTime() - now.getTime()

      if (difference <= 0) {
        setTimeRemaining(null)
        return
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24))
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      )
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((difference % (1000 * 60)) / 1000)

      setTimeRemaining(`${days}d ${hours}h ${minutes}m ${seconds}s`)
    }

    const intervalId = setInterval(calculateTimeRemaining, 1000)
    calculateTimeRemaining()

    return () => clearInterval(intervalId)
  }, [targetDate])

  if (!dict) {
    return null // lub loading spinner
  }

  const { title, message } = dict[type]
  const displayMessage = targetDate
    ? message.withCountdown
    : message.withoutCountdown

  return (
    <div className={s.container}>
      <div className={s.content}>
        <div className={s.logo}>
          <Image
            src={zebraLogoSrc}
            alt="Zebra Logo"
            width={200}
            height={58}
            style={{ width: '100%', height: 'auto' }}
          />
        </div>

        <div className={s.text}>
          <h1>{title}</h1>
          <p className={s.message}>{displayMessage}</p>
          {targetDate && (
            <div className={s.counter}>
              <p className={s.countdown}>{timeRemaining}</p>
              <p className={s.startDate}>
                {dict.start}: {formatDate(targetDate)}
              </p>
            </div>
          )}
        </div>
      </div>
      <div className={s.imageContainer}>
        <div className={s.image}>
          <Image src="/photos/street.webp" alt={title} fill objectFit="cover" />
        </div>
      </div>
    </div>
  )
}
